<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
  mediaUpload: {
    type: Boolean,
    default: false,
  },
  initialProcessedParams: {
    type: Object,
    default: undefined,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');

const processedParams = ref({});

// Mirrors the WhatsApp template header limits enforced on the Campaign model.
const MEDIA_UPLOAD_SIZE_LIMITS = { image: 5, video: 16, document: 100 };
const MEDIA_UPLOAD_ACCEPT = {
  image: 'image/jpeg,image/png',
  video: 'video/mp4',
  document: 'application/pdf',
};

const mediaBlobId = ref(null);
const uploadedMediaName = ref('');
const mediaUploadError = ref('');
const isUploadingMedia = ref(false);
const useMediaUrlInput = ref(false);
const mediaFileInputRef = ref(null);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const headerText = computed(() => {
  return headerComponent.value?.format === 'TEXT'
    ? headerComponent.value?.text || ''
    : '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const headerFormat = computed(() =>
  headerComponent.value?.format?.toLowerCase()
);

const mediaUploadSizeLimit = computed(
  () => MEDIA_UPLOAD_SIZE_LIMITS[headerFormat.value] || 5
);

const mediaUploadAccept = computed(
  () => MEDIA_UPLOAD_ACCEPT[headerFormat.value] || ''
);

const hasBodyVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasTextHeaderVariables = computed(() => {
  return headerText.value?.match(/{{([^}]+)}}/g) !== null;
});

const hasVariables = computed(
  () => hasBodyVariables.value || hasTextHeaderVariables.value
);

const renderedHeader = computed(() => {
  return renderTemplatePreview(
    headerText.value,
    processedParams.value.header || {}
  );
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const templateValidationParams = computed(() => {
  if (!props.mediaUpload || useMediaUrlInput.value || !mediaBlobId.value) {
    return processedParams.value;
  }

  return {
    ...processedParams.value,
    header: {
      ...processedParams.value.header,
      media_url: mediaBlobId.value,
    },
  };
});

const isFormInvalid = computed(() => {
  if (
    props.mediaUpload &&
    !useMediaUrlInput.value &&
    (isUploadingMedia.value || mediaUploadError.value)
  ) {
    return true;
  }

  return !isWhatsAppComplete(props.template, templateValidationParams.value);
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const mergeProcessedParams = (defaults, initial) => {
  if (Array.isArray(defaults)) {
    return initial === undefined
      ? defaults.map(value => mergeProcessedParams(value))
      : initial.map(value => mergeProcessedParams(value));
  }
  if (defaults && typeof defaults === 'object') {
    const initialObject = initial && typeof initial === 'object' ? initial : {};
    return Object.fromEntries(
      [
        ...new Set([...Object.keys(defaults), ...Object.keys(initialObject)]),
      ].map(key => [
        key,
        mergeProcessedParams(defaults[key], initialObject[key]),
      ])
    );
  }

  return initial === undefined ? defaults : initial;
};

const initializeTemplateParameters = () => {
  const defaults = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
  processedParams.value = mergeProcessedParams(
    defaults,
    props.initialProcessedParams
  );
  useMediaUrlInput.value = Boolean(
    props.mediaUpload && props.initialProcessedParams?.header?.media_url
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const clearMediaSelection = ({ clearUrl = false } = {}) => {
  mediaBlobId.value = null;
  uploadedMediaName.value = '';
  mediaUploadError.value = '';

  if (clearUrl) {
    updateMediaUrl('');
    if (isDocumentTemplate.value) updateMediaName('');
  }
};

const resetMediaUpload = () => {
  clearMediaSelection();
  useMediaUrlInput.value = false;
};

const onMediaUrlInput = value => {
  updateMediaUrl(value);
  clearMediaSelection();
};

const showMediaUrlInput = () => {
  clearMediaSelection({ clearUrl: true });
  useMediaUrlInput.value = true;
};

const showMediaUploadInput = () => {
  clearMediaSelection({ clearUrl: true });
  useMediaUrlInput.value = false;
};

const onMediaFileSelected = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  clearMediaSelection({ clearUrl: true });
  if (!checkFileSizeLimit(file, mediaUploadSizeLimit.value)) {
    mediaUploadError.value = t(
      'WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_SIZE_ERROR',
      {
        size: mediaUploadSizeLimit.value,
      }
    );
    return;
  }

  isUploadingMedia.value = true;
  try {
    const { fileUrl, blobId } = await uploadFile(file, accountId.value);
    updateMediaUrl(fileUrl);
    mediaBlobId.value = blobId;
    uploadedMediaName.value = file.name;
    if (isDocumentTemplate.value) updateMediaName(file.name);
  } catch (error) {
    mediaUploadError.value = t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_ERROR');
  } finally {
    isUploadingMedia.value = false;
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid || isFormInvalid.value) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: props.sendRenderedContent
      ? renderedTemplate.value
      : bodyText.value,
    pendingMessageContent: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
      processed_params: processedParams.value,
    },
    ...(mediaBlobId.value ? { mediaBlobId: mediaBlobId.value } : {}),
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => [props.template, props.initialProcessedParams],
  () => {
    resetMediaUpload();
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedHeader,
  renderedTemplate,
  isFormInvalid,
  v$,
  mediaBlobId,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div
            v-if="renderedHeader"
            class="mb-2 text-sm font-medium whitespace-pre-wrap text-n-slate-12"
          >
            {{ renderedHeader }}
          </div>
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>

        <template v-if="mediaUpload && !useMediaUrlInput">
          <div class="flex items-center gap-2 mb-2.5">
            <Button
              type="button"
              variant="outline"
              color="slate"
              size="sm"
              :disabled="isUploadingMedia"
              :is-loading="isUploadingMedia"
              :label="
                t('WHATSAPP_TEMPLATES.PARSER.MEDIA_UPLOAD_BUTTON', {
                  type: formatType,
                })
              "
              @click="mediaFileInputRef?.click()"
            />
            <span
              v-if="uploadedMediaName"
              class="text-xs truncate text-n-green-11"
            >
              {{ uploadedMediaName }}
            </span>
          </div>
          <input
            ref="mediaFileInputRef"
            type="file"
            class="hidden"
            :accept="mediaUploadAccept"
            @change="onMediaFileSelected"
          />
          <p v-if="mediaUploadError" class="mb-2.5 text-xs text-n-ruby-11">
            {{ mediaUploadError }}
          </p>
          <button
            type="button"
            class="text-xs underline underline-offset-2 text-n-slate-11"
            @click="showMediaUrlInput"
          >
            {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_USE_URL') }}
          </button>
        </template>

        <template v-else>
          <div class="flex items-center mb-2.5">
            <Input
              :model-value="processedParams.header?.media_url || ''"
              type="url"
              class="flex-1"
              :placeholder="
                t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                  type: formatType,
                })
              "
              @update:model-value="onMediaUrlInput"
            />
          </div>
          <button
            v-if="mediaUpload"
            type="button"
            class="text-xs underline underline-offset-2 text-n-slate-11"
            @click="showMediaUploadInput"
          >
            {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_USE_UPLOAD') }}
          </button>
        </template>

        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <div v-if="hasTextHeaderVariables && processedParams.header">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.header"
          :key="`header-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.header[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
